import type { ReactNode } from "react";
import styles from "./WorkflowActionBar.module.css";

export interface WorkflowActionBarProps {
  /** Optional guidance or status shown above the controls. */
  status?: ReactNode;
  /** Live region role for `status` (errors should use `alert`). */
  statusRole?: "status" | "alert";
  /** Secondary/navigation actions aligned to the start of the bar. */
  leadingActions?: ReactNode;
  /** Primary workflow actions aligned to the end of the bar. */
  trailingActions?: ReactNode;
  /** Accessible region name. */
  ariaLabel?: string;
  /**
   * When false, the bar stays in document flow so a parent can own sticky
   * placement (e.g. stacked with a bulk-action toolbar). Default true.
   */
  sticky?: boolean;
}

/**
 * Persistent action surface for long forms and review/import workflows.
 *
 * Domain components own button labels, variants, disabled rules, and status
 * semantics. This component only standardises sticky placement and responsive
 * layout.
 */
export function WorkflowActionBar({
  status,
  statusRole = "status",
  leadingActions,
  trailingActions,
  ariaLabel = "Workflow actions",
  sticky = true,
}: WorkflowActionBarProps) {
  return (
    <section
      className={`${styles.actionBar}${sticky ? "" : ` ${styles.actionBarInFlow}`}`}
      aria-label={ariaLabel}
    >      {status && (
        <div className={styles.status} role={statusRole}>
          {status}
        </div>
      )}
      {leadingActions && (
        <div className={styles.leading}>{leadingActions}</div>
      )}
      {trailingActions && (
        <div className={styles.trailing}>{trailingActions}</div>
      )}
    </section>
  );
}
